import type {FC, JSX} from "react";

import type {StepWizardChildProps} from "react-step-wizard";

import {ReportForm} from "./ReportForm";
import {createReport} from "../../actions";
import {descriptions} from "../../constants/descriptions";
import {useAppDispatch, useAppSelector} from "../../hooks";
import type {PlanState, ProductsList, ProductsListEntry, ProductsListOptions} from "../../reducers/types";
import type {RootState} from "../../store";
import {
  formatNumberToCurrencyValue,
  formatNumberToVatValue,
  isRecord,
  useWizardNavigation
} from "../../utils/app-utils";
import Drawing from "../Drawing";
import {WizardNavigationButtons} from "./WizardNavigationButtons";
import {useErrorNotifications} from "../common/Notifications";


/**
 * Wizard summary step component.
 */
export const Summary: FC<Partial<StepWizardChildProps>> = (props) => {
  const plan = useAppSelector<PlanState>((state) => state.plan)
  const navigation = useWizardNavigation(props)

  return (
    <div>
      <div className='row mb-3'>
        <div className='col-12 col-md-12'>
          <Drawing enableUpdating={navigation.isStepCurrentlyActive}/>
        </div>
      </div>
      <div className='row mb-3'>
        <SummaryColumn plan={plan}/>
        <PriceEstimateColumn plan={plan}/>
      </div>
      <WizardNavigationButtons testIdPrefix='summary' isLastStep navigation={navigation} isValid/>
    </div>
  )
}

/* Child Components */

const SummaryColumn: FC<{ plan: PlanState }> = ({plan}) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- this should be accurate type
  const objKeys = Object.keys(plan) as Array<keyof PlanState>
  const keys: Array<keyof PlanState> = objKeys.filter((k: keyof PlanState) => (plan[k] !== undefined && plan[k] !== ''))
  const filteredKeys: Array<keyof PlanState> = objKeys.filter(
    (k: keyof PlanState) => (
      k !== 'temperatureMetersEnabled' && k !== 'wirelessMode' && k !== 'smokeDetectorType'
    )
  )

  return (
    <div className='col-12 col-md-6'>
      <h4 data-testid='summary-heading' className='mb-4'>Yhteenveto</h4>
      <div className='card shadow-sm border-0 mb-4 overflow-hidden'>
        <table data-testid='summary-table' className='table table-striped mb-0'>
          <SummaryRow summaryKeys={plan.systemType === 'wired' ? filteredKeys : keys}/>
        </table>
      </div>
    </div>
  )
}

const PriceEstimateColumn: FC<{ plan: PlanState }> = ({plan}) => {
  const dispatch = useAppDispatch()
  const productsList = useAppSelector((state: RootState) => state.products.list)
  const productsError = useAppSelector<string>((state) => state.products.error)
  useErrorNotifications((state) => state.products.error)

  function onCreateReport(contact: boolean, email: string, message: string) {
    dispatch(createReport({contact, email, plan, message}))
  }

  const isComplexWirelessSystem = (plan.buildingQuantity ?? 0) > 1 && plan.systemType === 'wireless'

  return (
    <div className='col-12 col-md-6'>
      <h4 className='mb-4'>Hinta-arvio</h4>
      {!productsError && <ProductOptions productsList={productsList}/>}
      <PriceInformationDisclaimer plan={plan}/>
      {isComplexWirelessSystem && <ComplexWirelessSystemDisclaimer/>}
      <ReportForm onCreateClick={onCreateReport}/>
    </div>
  )
}

const ProductOptions: FC<{ productsList: ProductsListOptions | null }> = ({productsList}) => {
  if (!productsList) {
    return null
  }

  if (productsList.ultrasonic) {
    return (
      <>
        <h5 className='mb-3'>Vaihtoehto 1: Ultraäänitoimisiset mittarit</h5>
        <ShoppingTable list={productsList.ultrasonic} testIdSuffix='-ultrasonic'/>
        <h5 className='mb-3'>Vaihtoehto 2: Induktiiviset mittarit</h5>
        <ShoppingTable list={productsList.inductive} testIdSuffix='-inductive'/>
      </>
    )
  }

  return <ShoppingTable list={productsList.inductive}/>
}

const ShoppingTable: FC<{ list: ProductsList, testIdSuffix?: string }> = ({list, testIdSuffix = ''}) => (
  <div className='card shadow-sm border-0 mb-4 overflow-hidden'>
    <table data-testid={`products-table${testIdSuffix}`} className='table table-striped mb-0'>
      <tbody style={{fontWeight: 'normal'}}>
      {list.entries.map(entry => (
        <ShoppingEntry key={entry.description} entry={entry}/>
      ))}
      </tbody>
      <ShoppingSum totalCost={list.totalCost}/>
    </table>
  </div>
)

const ComplexWirelessSystemDisclaimer: FC = () => (
  <div className='alert alert-warning d-flex justify-content-between' role='alert'>
    Huomioithan, että useamman rakennuksen langattomat järjestelmät ovat hyvinkin erilaisia keskenään, eikä
    suunnittelukoneella pystytä luotettavasti suunnittelemaan näitä.
    Ole siis yhteydessä myyntiin saadaksesi tarkemman hinnan!
  </div>
)

const PriceInformationDisclaimer: FC<{ plan: PlanState }> = ({plan}) => {
  const pricing = useAppSelector((state: RootState) => state.servicePrices.pricing)

  if (plan.wirelessMode === 'hydrolinkAir') {
    // Do not show this disclaimer for wireless Hydrolink Air system
    return null
  }

  const prmPrice = formatNumberToCurrencyValue(pricing.hydrolinkPremiumPrice)
  const apiPrice = formatNumberToCurrencyValue(pricing.hydrolinkApiPrice)

  return (
    <div className='mb-4'>
      <p>
        HYDROLINK Online -palvelumme on täysin maksuton.
      </p>
      <p>
        HYDROLINK Online Premium -palvelun vuosilisenssi taloyhtiölle on {prmPrice} (alv 0%). Ei erikseen mittari- tai
        asuntokohtaisia maksuja. Premium-palvelussa mm. vuotovalvonta sekä mobiilisovellukset asukkaille oman
        huoneiston vedenkulutuksen seurantaan. Tarjoamme ensimmäisen vuoden kokeilujaksona veloituksetta.
      </p>
      <p>
        HYDROLINK Online API -paketin vuosilisenssi taloyhtiölle on {apiPrice} (alv 0%). API-paketin avulla saadaan
        siirrettyä Hydrolink-järjestelmän lukema- sekä lämpötilatiedot automatisoidusti kolmannen osapuolen
        ohjelmistoihin.
      </p>
    </div>
  )
}

const SummaryRow = ({summaryKeys}: { summaryKeys: Array<keyof PlanState> }): JSX.Element => {
  const plan = useAppSelector<PlanState>((state) => state.plan)

  return (
    <tbody style={{fontWeight: 'normal'}}>
    {
      summaryKeys.map((key: keyof PlanState) => (
        <tr key={key}>
          <td>
            {descriptions[key].description}
          </td>
          <td className='text-end'>
            <strong>{getPlanPropertyDescription(plan, key)}</strong>
          </td>
        </tr>
      ))
    }
    </tbody>
  )
}

interface ShoppingEntryProps {
  entry: ProductsListEntry
}

function ShoppingEntry(props: Readonly<ShoppingEntryProps>): JSX.Element {
  return (
    <tr>
      <td>
        {props.entry.description}
      </td>
      <td className='text-end'>
        <small className='text-muted'>x{props.entry.quantity}</small>
      </td>
    </tr>
  )
}

function ShoppingSum(props: Readonly<{ totalCost: number }>): JSX.Element {

  const vatMultiplier = useAppSelector<number>((state: RootState) => state.servicePrices.pricing.vatMultiplier)

  const priceBeforeTax = props.totalCost / 100
  const priceAfterTax = (props.totalCost * vatMultiplier) / 100
  const vatPercentage = formatNumberToVatValue(((vatMultiplier - 1.0) * 100))

  const strPriceBeforeTax = formatNumberToCurrencyValue(priceBeforeTax)
  const strPriceAfterTax = formatNumberToCurrencyValue(priceAfterTax)
  return (
    <tfoot>
    <tr>
      <td><strong>Yhteensä (alv 0%)</strong></td>
      <td className='text-end'><strong>{strPriceBeforeTax}</strong></td>
    </tr>
    <tr className='table-info'>
      <td><strong>Yhteensä (alv {vatPercentage}%)</strong></td>
      <td className='text-end'><strong>{strPriceAfterTax}</strong></td>
    </tr>
    </tfoot>
  )
}

/* Helper Functions */

function getPlanPropertyDescription(plan: PlanState, key: keyof PlanState): string {
  // TODO: this function should be rewritten into clearly typed logic
  const planField: string | number = plan[key] ?? ''
  // eslint-disable-next-line @typescript-eslint/prefer-destructuring -- fix with refactoring this method
  const descriptionStructureField = descriptions[key]

  // @ts-expect-error -- TODO: fix this with proper type
  const propertyDescHolder: unknown = descriptionStructureField[planField]
  if (!propertyDescHolder) {
    return String(planField)
  }

  if (isRecord(propertyDescHolder)) {
    if (typeof propertyDescHolder.description === 'string') {
      return propertyDescHolder.description
    }
  }

  return String(planField)
}
